import { CSSObject, Theme, ThemeOptions } from '@mui/material';
import { ThemeColorKey } from './types';
import { ResponsiveCSSProperties } from './customTheme';
import _merge from 'lodash/merge';

export const getThemeColor = (theme: Theme | null | undefined, color: string): string => {
  if (theme?.vars.palette && color in theme.vars.palette) {
    if (color === 'text') {
      return theme.vars.palette.text.primary;
    }
    const paletteColor = theme.vars.palette[color as keyof typeof theme.vars.palette];
    if (paletteColor && typeof paletteColor === 'object') {
      if ('main' in paletteColor) {
        return paletteColor.main;
      }
      if ('500' in paletteColor) {
        return paletteColor[500] as string;
      }
    }
  }
  return color;
};

export const resolveThemeColor = (theme: Theme, color: ThemeColorKey) => {
  return {
    ...theme.vars.palette[color],
    contrastText: theme.vars.palette[color]?.contrastText,
  };
};

export const createResponsiveStyles = (
  theme: Theme,
  ...values: (ResponsiveCSSProperties | undefined)[]
): Record<string, React.CSSProperties> => {
  const styles = values
    .map((responsiveStyle) => {
      if (!responsiveStyle) {
        return;
      }
      const styles = {
        // Match [0, xs)
        [theme.breakpoints.down('xs')]: responsiveStyle.xs,
        // Match [xs, ∞)
        [theme.breakpoints.up('xs')]: responsiveStyle.xs,
        // Match [sm, ∞)
        [theme.breakpoints.up('sm')]: responsiveStyle.sm,
        // Match [md, ∞)
        [theme.breakpoints.up('md')]: responsiveStyle.md,
        // Match [lg, ∞)
        [theme.breakpoints.up('lg')]: responsiveStyle.lg,
        // Match [xl, ∞)
        [theme.breakpoints.up('xl')]: {
          ...responsiveStyle.xl,
        },
      };
      return styles;
    })
    .filter((style) => !!style);
  return _merge({}, ...styles);
};

export const isThemeDirty = ({
  initialTheme,
  themeFromSetup,
  currentTheme,
}: {
  initialTheme: ThemeOptions | null | undefined;
  currentTheme: ThemeOptions | null | undefined;
  themeFromSetup: ThemeOptions | null | undefined;
}) => {
  try {
    const setup = themeFromSetup && Object.keys(themeFromSetup).length > 0 ? JSON.stringify(themeFromSetup) : undefined;
    const init = Object.keys(initialTheme ?? {}).length > 0 ? JSON.stringify(initialTheme) : undefined;
    const curr = JSON.stringify(currentTheme ?? {});
    if (setup && setup !== init) {
      return setup !== curr;
    }
    return init && init !== curr;
  } catch (error) {
    console.error('[ThemeSetup] Failed to check if theme has changed', error);
    return false;
  }
};

export const resolveThemeTypographyOptions = (theme: ThemeOptions | undefined) => {
  if (typeof theme?.typography === 'function') {
    return undefined;
  }
  return theme?.typography;
};

export const resolveThemeStyleOverrides = (
  theme: Theme | ThemeOptions,
  componentName: 'BCSolidButton' | 'BCOutlineButton' | 'BCTextButton' | 'BCIconButton',
) => {
  const styleOverrides = theme.components?.[componentName]?.styleOverrides?.root;
  if (!styleOverrides || typeof styleOverrides !== 'object') {
    return undefined;
  }
  return styleOverrides as CSSObject;
};
